/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useMemo } from 'react';

import { useAppContext } from '../../../../common/ui/app-context';
import { getWorkItemPageUrl } from '../../../../common/utils/quick-find/jira-work-item-navigation';
import { useSelectedFilters } from '../../../../controllers/filters/selected';
import { useQuickFindQuery } from '../../../../controllers/store/quick-find';
import { ISSUE_NAVIGATOR_URL } from '../search-page-link/utils';

import type { IntentMode } from './types';
import { isSamePathname } from './url-matching';

export type IntentRoutingDisabledResult = {
	disabled: boolean;
	initialPrimaryMode: IntentMode;
};

/**
 * Context-aware disablement.
 *
 * If the user is already on the All apps search page OR Jira work items page,
 * disable intent routing and pin the primary row to that page.
 */
export const useIntentRoutingDisabled = (): IntentRoutingDisabledResult => {
	const { generateSearchUrl, queryParams } = useAppContext();
	const quickFindQuery = useQuickFindQuery();
	const {
		selectedFilters: { jiraFilters },
	} = useSelectedFilters({});

	const allAppsUrl = useMemo(
		() =>
			generateSearchUrl({
				...queryParams,
				text: quickFindQuery,
			}),
		[generateSearchUrl, queryParams, quickFindQuery],
	);

	const jiraWorkItemsUrl = useMemo(() => {
		try {
			return getWorkItemPageUrl({
				query: quickFindQuery,
				jiraFilters,
				generateSearchUrl,
				queryParams,
			});
		} catch {
			return ISSUE_NAVIGATOR_URL;
		}
	}, [generateSearchUrl, jiraFilters, queryParams, quickFindQuery]);

	const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

	const onAllAppsPage = useMemo(
		() => (currentUrl ? isSamePathname(currentUrl, allAppsUrl) : false),
		[currentUrl, allAppsUrl],
	);
	const onJiraWorkItemsPage = useMemo(
		() => (currentUrl ? isSamePathname(currentUrl, jiraWorkItemsUrl) : false),
		[currentUrl, jiraWorkItemsUrl],
	);

	return {
		disabled: onAllAppsPage || onJiraWorkItemsPage,
		initialPrimaryMode: onJiraWorkItemsPage ? 'JIRA_WORK_ITEMS' : 'ALL_APPS',
	};
};
